import { Box, Button, Skeleton } from '@mui/material';
import { useRouter } from 'next/navigation';
import { useContext, useState } from 'react';
import type { ReactNode } from 'react';

import { AppContext } from '../../App';
import { caloTheme } from '../../assets/themes/calo';
import { useHomeBatches } from '../../hooks/useBatchTrackingQueries';
import useHasPermission from '../../hooks/useHasPermission';
import useNow from '../../hooks/useNow';
import useTodayProgress from '../../hooks/useTodayProgress';
import { goalMet, KDSPermission } from '../../libs';
import type { Kitchen, TodayProgress } from '../../libs';
import { usesKeycloak } from '../../libs/keycloakSession';
import { getFirstPermittedRoute } from '../../libs/utils/getFirstPermittedRoute';
import { dayInKitchen } from '../../libs/utils/kitchenDay';
import type { Batch } from '../BatchTracking/types';
import { outlineButtonSx, primaryButtonSx, quietLinkSx } from '../chefStyles';
import EndMessage from '../EndMessage';
import FocusedHeading from '../FocusedHeading';
import AccountActivityCard from './AccountActivityCard';
import AddMenu from './AddMenu';
import AllDone from './AllDone';
import HomeHeader from './HomeHeader';
import HomeTimeline, { rowSx } from './HomeTimeline';
import ManualBatchDialog from './ManualBatchDialog';
import ProgressDots from './ProgressDots';
import type { HomeTask } from './tasks';
import { buildTimeline, effectiveNow } from './timeline';
import useHomeTasks from './useHomeTasks';

const { palette } = caloTheme;

const Page = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      width: '100%',
      maxWidth: 768,
      mx: 'auto',
      minWidth: 0,
      boxSizing: 'border-box',
      px: { xs: 2, sm: 3 },
      pt: { xs: 2, md: 4 },
      pb: { xs: 4, md: 6 }
    }}
  >
    {children}
  </Box>
);

const skeletonSx = { bgcolor: palette.neutral100, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } };

// The shape of the loaded list: the heading, then rows on the timeline grid.
const Loading = () => (
  <>
    <Skeleton
      variant="rounded"
      sx={{ ...skeletonSx, width: { xs: 160, md: 220 }, height: { xs: 32, md: 36 }, mt: { xs: 1, md: 1.5 }, borderRadius: '8px' }}
    />
    <Box sx={{ mt: { xs: 3, md: 4 } }}>
      {[0, 1, 2].map((key) => (
        <Box key={key} sx={{ ...rowSx, pb: { xs: 2, md: 3 } }}>
          <Skeleton
            variant="rounded"
            sx={{ ...skeletonSx, width: { xs: 40, md: 48 }, height: 20, justifySelf: 'end', mt: { xs: '20px', md: '24px' } }}
          />
          <Box />
          <Skeleton variant="rounded" sx={{ ...skeletonSx, height: { xs: 80, md: 96 }, borderRadius: '16px' }} />
        </Box>
      ))}
    </Box>
  </>
);

type TaskSource = {
  tasks: HomeTask[];
  progress: TodayProgress | undefined;
  loading: boolean;
  error: boolean;
  reload: () => void;
};

// A day in time order: the user's batches and, for a head chef (`tasks` set), every batch of the kitchen and their tasks.
// Once the day's clip goal is met the clips step aside until asked for; nothing is kept here, so the next read shows what changed.
const HomeScreen = ({ kitchen, tasks }: { kitchen: Kitchen; tasks: TaskSource | undefined }) => {
  const now = useNow();
  const batches = useHomeBatches(kitchen, dayInKitchen(now, kitchen));
  const [showMore, setShowMore] = useState(false);
  const [dialog, setDialog] = useState<{ batch?: Batch }>();
  const router = useRouter();
  const { user } = useContext(AppContext);
  const leaveRoute = getFirstPermittedRoute(user.permissions ?? []);
  // The overview needs both permissions; the server answers it for Cognito and Hub accounts alike.
  const canViewAccounts = useHasPermission(KDSPermission.HEAD_CHEF, KDSPermission.VIEW_BATCH_TRACKING);

  const headChef = tasks !== undefined;
  const allTasks = tasks?.tasks ?? [];
  // With no task on the list a head chef's Home is the batches, as for anyone else: the clip goal and its dots belong to the tasks.
  const withTasks = allTasks.length > 0;
  const clock = effectiveNow(now, batches.batches);
  const goalDone = goalMet(tasks?.progress) && !showMore ? tasks?.progress : undefined;
  const shown = goalDone === undefined ? allTasks : allTasks.filter((task) => task.kind !== 'videoReview');
  const clipsWaiting = shown.length < allTasks.length;
  // Refused for this kitchen: Add is hidden and nothing says why; the account just has nothing to record.
  const refused = batches.unauthorized;
  const reload = () => {
    tasks?.reload();
    batches.reload();
  };

  const content = () => {
    if (tasks?.loading || batches.loading) {
      return <Loading />;
    }
    if (tasks?.error || (batches.error && !refused)) {
      return (
        <EndMessage
          title={tasks?.error ? "Couldn't load today's tasks" : "Couldn't load today's batches"}
          line="Check the connection, then try again."
          action={
            <Button variant="contained" sx={primaryButtonSx} onClick={reload}>
              Try again
            </Button>
          }
        />
      );
    }
    if (shown.length === 0 && batches.batches.length === 0) {
      if (goalDone !== undefined) {
        return (
          <AllDone
            progress={goalDone}
            moreWaiting={clipsWaiting}
            onMore={() => setShowMore(true)}
            onLeave={leaveRoute === undefined ? undefined : () => router.push(leaveRoute)}
          />
        );
      }
      return headChef ? (
        <EndMessage
          title="Nothing on your list today"
          action={
            <Button variant="outlined" sx={outlineButtonSx} onClick={reload}>
              Check again
            </Button>
          }
        />
      ) : (
        <EndMessage title="Nothing recorded today" line={refused ? undefined : 'Use Add to record a batch.'} />
      );
    }
    return (
      <>
        <Box sx={{ mt: { xs: 1, md: 1.5 } }}>
          <FocusedHeading>{withTasks ? "Today's tasks" : "Today's batches"}</FocusedHeading>
        </Box>
        {withTasks && tasks?.progress !== undefined && (
          <Box sx={{ mt: { xs: 1.5, md: 2 } }}>
            <ProgressDots done={tasks.progress.done} goal={tasks.progress.goal} />
          </Box>
        )}
        {clipsWaiting && (
          <Button variant="text" sx={{ ...quietLinkSx, mt: 1, ml: -1.5 }} onClick={() => setShowMore(true)}>
            More clips are waiting ›
          </Button>
        )}
        <Box sx={{ mt: { xs: 3, md: 4 } }}>
          <HomeTimeline
            entries={buildTimeline(shown, batches.batches, clock)}
            kitchen={kitchen}
            now={clock}
            label={withTasks ? "Today's tasks" : "Today's batches"}
            onOpen={(route) => router.push(route)}
            onEditBatch={(batch) => setDialog({ batch })}
          />
        </Box>
      </>
    );
  };

  return (
    <Page>
      <HomeHeader kitchen={kitchen} now={now}>
        {!refused && <AddMenu options={[{ label: 'Manual batch', onSelect: () => setDialog({}) }]} />}
      </HomeHeader>
      {canViewAccounts && <AccountActivityCard kitchen={kitchen} today={dayInKitchen(now, kitchen)} />}
      {content()}
      {dialog !== undefined && (
        <ManualBatchDialog
          kitchen={kitchen}
          batch={dialog.batch}
          now={clock}
          onClose={() => setDialog(undefined)}
          onRefused={batches.reload}
        />
      )}
    </Page>
  );
};

const HeadChefHome = ({ kitchen }: { kitchen: Kitchen }) => {
  const { tasks, loading, error, reload } = useHomeTasks(kitchen);
  const { progress, loading: progressLoading } = useTodayProgress(kitchen);
  return <HomeScreen kitchen={kitchen} tasks={{ tasks, progress, loading: loading || progressLoading, error, reload }} />;
};

// Home is for every signed-in user; the task queries need e:hc and a Calo Hub session, so only a Hub head chef asks them.
const Home = ({ kitchen }: { kitchen: Kitchen }) => {
  const { user } = useContext(AppContext);
  return usesKeycloak() && user.permissions?.includes(KDSPermission.HEAD_CHEF) ? (
    <HeadChefHome kitchen={kitchen} />
  ) : (
    <HomeScreen kitchen={kitchen} tasks={undefined} />
  );
};

export default Home;
